import React, { useRef, useState, useEffect } from "react";
import { Checkbox } from "./ui/checkbox";
import { galleryAvailable, getSaveToGalleryPref, setSaveToGalleryPref } from "../lib/gallery";
import { createPortal } from "react-dom";
import { api, sendMessage, mediaSrc, uploadMedia, dataUrlToBase64 } from "../api";
import { useApp } from "../context/AppContext";
import { MarkupEditor } from "./MarkupEditor";
import { toast } from "sonner";
import { X, PenLine, Loader2, Video, Send, Subtitles, Share2, Download, MessageCircle, Link2, Forward } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { shareNative, shareWhatsApp, copyLink, saveFiles, saveToDevice, canShareFiles, mediaFilename } from "../lib/share";
import { ForwardDialog } from "./ForwardDialog";
import { playbackFocusAcquire, playbackFocusRelease } from "../lib/wakeword";

function fmtSecs(s) {
  const m = Math.floor((s || 0) / 60);
  const ss = Math.floor((s || 0) % 60);
  return `${m}:${String(ss).padStart(2, "0")}`;
}

/**
 * Fullscreen video player (like the photo/PDF viewers).
 * Pause anywhere -> "Markup" captures the current frame, opens the MarkupEditor,
 * and sending posts the annotated frame into the conversation as a reply to the video.
 * The video then resumes from where it stopped. Repeatable as many times as needed.
 */
export function VideoViewer({ open, media, message, conversation, markups = [], onClose, onSend, expectCaptions = false }) {
  const [saveGallery, setSaveGallery] = useState(getSaveToGalleryPref);
  const { user, t, lang } = useApp();
  const videoRef = useRef(null);
  const barRef = useRef(null);
  const [frame, setFrame] = useState(null); // {dataUrl, time}
  const [sending, setSending] = useState(false);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  const [captions, setCaptions] = useState(null); // null = loading/unknown; {segments: [...]}
  const [ccOn, setCcOn] = useState(true); // captions ON by default
  const [generating, setGenerating] = useState(false); // on-demand server-side subtitles (gallery videos)
  const [forwardOpen, setForwardOpen] = useState(false);
  const shareName = media ? mediaFilename(media) : "video.mp4";

  // Video has sound = media PLAYBACK: take the iOS playback focus (stops the
  // "Hey Buddy" recognizer) BEFORE the <video> mounts, release on close
  const [focusReady, setFocusReady] = useState(false);
  useEffect(() => {
    if (!open) { setFocusReady(false); return undefined; }
    let held = false;
    let alive = true;
    playbackFocusAcquire().then(() => { if (alive) { held = true; setFocusReady(true); } else playbackFocusRelease(); });
    return () => { alive = false; if (held) playbackFocusRelease(); };
  }, [open]);

  // Auto captions: fetch (and poll while they are being generated after a recording)
  const mediaId = media?.id;
  useEffect(() => {
    setCaptions(null);
    setGenerating(false);
    if (!open || !mediaId || String(mediaId).startsWith("local-")) return undefined;
    let alive = true;
    let tries = 0;
    const maxTries = expectCaptions || generating ? 60 : 3; // keep polling while Whisper runs
    const load = async () => {
      try {
        const res = await api.get(`/media/${mediaId}/captions`);
        if (!alive) return;
        if (res.data?.status === "ready") {
          setCaptions({ lang: res.data.lang, segments: res.data.segments || [] });
          setGenerating(false);
          return;
        }
        if (res.data?.status === "generating") setGenerating(true);
      } catch (e) { /* not ready / network — retry below */ }
      tries += 1;
      if (alive && (tries < maxTries || generating)) setTimeout(load, 3000);
      else if (alive) setCaptions({ segments: [] }); // no captions available
    };
    load();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mediaId, expectCaptions, generating]);

  const requestCaptions = async () => {
    if (!mediaId || String(mediaId).startsWith("local-")) return;
    try {
      const res = await api.post(`/media/${mediaId}/captions/auto`);
      if (res.data?.status === "ready") { setCaptions({ lang: res.data.lang, segments: res.data.segments || [] }); setCcOn(true); return; }
      setGenerating(true);
      toast.info(t("generating_captions"));
    } catch (e) { toast.error(t("something_wrong")); }
  };

  if (!open || !media) return null;

  const hasCaptions = !!(captions && captions.segments && captions.segments.length > 0);
  const captionsLoading = (expectCaptions && captions === null) || generating;
  const captionsEmpty = !!captions && !hasCaptions && !generating; // generated, but nothing to show (no speech)
  const activeSeg = ccOn && hasCaptions
    ? captions.segments.find((s) => progress >= s.start - 0.15 && progress <= s.end + 0.35)
    : null;
  const captionText = activeSeg ? ((lang === "pt" ? activeSeg.pt : activeSeg.en) || activeSeg.text) : null;

  const seekTo = (time) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.max(0, Math.min(time, v.duration || time));
  };

  const onBarClick = (e) => {
    const bar = barRef.current;
    const v = videoRef.current;
    if (!bar || !v || !duration) return;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    seekTo(ratio * duration);
  };

  // NOTE: no crossOrigin attr on the main <video>: Safari/WKWebView refuses to
  // play blob: previews with it set. Cross-origin media (native app) is
  // captured through an offscreen CORS-enabled copy instead.
  const grabFrame = (el) => {
    const canvas = document.createElement("canvas");
    canvas.width = el.videoWidth || 1280;
    canvas.height = el.videoHeight || 720;
    canvas.getContext("2d").drawImage(el, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.9);
  };
  const waitEvent = (el, ev) => new Promise((res, rej) => {
    const t = setTimeout(() => rej(new Error("timeout")), 15000);
    el.addEventListener(ev, () => { clearTimeout(t); res(); }, { once: true });
    el.addEventListener("error", () => { clearTimeout(t); rej(new Error("video error")); }, { once: true });
  });
  const captureFrame = async () => {
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    const time = v.currentTime;
    try {
      setFrame({ dataUrl: grabFrame(v), time });
      return;
    } catch (e) { /* tainted canvas (cross-origin media) → CORS copy below */ }
    try {
      const off = document.createElement("video");
      off.crossOrigin = "anonymous";
      off.muted = true;
      off.playsInline = true;
      off.preload = "auto";
      off.src = mediaSrc(media);
      await waitEvent(off, "loadedmetadata");
      off.currentTime = time;
      await waitEvent(off, "seeked");
      setFrame({ dataUrl: grabFrame(off), time });
    } catch (e) {
      toast.error("Frame capture failed");
    }
  };

  // Close the editor and continue playing from where it was paused
  const resume = () => {
    setFrame(null);
    const v = videoRef.current;
    if (v) {
      try { v.play(); } catch (e) { /* ignore */ }
    }
  };

  const sendMarkup = async (editedDataUrl) => {
    const snap = frame;
    resume(); // back to the video right away — upload continues in background
    if (!snap || !conversation?.id) return;
    setSending(true);
    try {
      const jobId = message?.meta?.linked_job_id || conversation?.job_id || null;
      const uploaded = await uploadMedia({
        base64: dataUrlToBase64(editedDataUrl),
        jobId,
        uploadedBy: user.id,
        versionLabel: `Video ${fmtSecs(snap.time)}`,
      });
      const senderName = (conversation?.participants || []).find((p) => p.id === message?.sender_id)?.name || "";
      await sendMessage({
        conversation_id: conversation.id,
        sender_id: user.id,
        type: "photo",
        content: `\u{1F3A5} ${fmtSecs(snap.time)}`,
        media_ids: [uploaded.id],
        meta: {
          video_markup: true,
          video_time: Math.round(snap.time),
          video_media_id: media.id,
          ...(jobId && !conversation?.job_id ? { linked_job_id: jobId, link_visibility: "shared" } : {}),
        },
        reply_to: message ? {
          message_id: message.id,
          sender_id: message.sender_id,
          sender_name: senderName,
          content: `\u{1F3A5} ${t("video")} \u00b7 ${fmtSecs(snap.time)}`,
          type: "video",
          media_id: null,
        } : null,
      });
      toast.success(t("markup_sent"));
    } catch (e) {
      toast.error("Failed");
    } finally {
      setSending(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[90] bg-black flex flex-col pt-safe" data-testid="video-viewer">
      {/* Top bar */}
      <div className="flex items-center gap-2 px-4 py-3 shrink-0">
        <Video className="h-4 w-4 text-white/70" />
        <span className="text-sm text-white/90 font-medium truncate flex-1">{media.filename || t("video")}</span>
        {sending && (
          <span className="flex items-center gap-1.5 text-xs text-white/80" data-testid="video-markup-sending">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t("uploading")}...
          </span>
        )}
        {captionsLoading && (
          <span className="flex items-center gap-1 text-[11px] text-white/70" data-testid="video-captions-generating" title={t("generating_captions")}>
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> CC
          </span>
        )}
        {hasCaptions ? (
          <button
            data-testid="video-cc-toggle"
            onClick={() => setCcOn((v) => !v)}
            title={t("captions")}
            className={`h-9 w-9 rounded-full flex items-center justify-center transition-colors ${ccOn ? "bg-[hsl(var(--accent))] text-black" : "bg-white/15 hover:bg-white/25 text-white"}`}
          >
            <Subtitles className="h-4 w-4" />
          </button>
        ) : (!captionsLoading && (
          <button
            data-testid="video-cc-generate"
            onClick={captionsEmpty && captions?.lang === "" && captions?.segments ? () => toast.info(t("no_speech_detected")) : requestCaptions}
            title={t("generate_captions")}
            className="h-9 w-9 rounded-full flex items-center justify-center bg-white/15 hover:bg-white/25 text-white/80 transition-colors"
          >
            <Subtitles className="h-4 w-4" />
          </button>
        ))}
        {media?.id && !String(media.id).startsWith("local-") && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="video-share-button" title={t("share")} className="h-9 w-9 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition-colors">
                <Share2 className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="z-[300]">
              <DropdownMenuItem data-testid="video-save-gallery" onClick={async () => {
                const r = await saveToDevice([{ url: mediaSrc(media), filename: shareName, kind: "video" }]);
                if (r === "saved") toast.success(t("saved_to_gallery_n").replace("{n}", 1)); else if (r === "downloaded") toast.success(t("saved_to_device")); else if (r === false) toast.error(t("something_wrong"));
              }}>
                <Download className="h-4 w-4 mr-2" /> {t("save_to_gallery")}
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="video-share-native" onClick={async () => { const ok = await shareNative({ url: mediaSrc(media), filename: shareName, title: shareName }); if (!ok) toast.info(t("share_unavailable")); }}>
                <Share2 className="h-4 w-4 mr-2" /> {t("share")}...
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="video-share-forward" onClick={() => setForwardOpen(true)}>
                <Forward className="h-4 w-4 mr-2" /> {t("send_to_chat")}
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="video-share-whatsapp" onClick={async () => {
                if (await canShareFiles()) { const r = await saveFiles([{ url: mediaSrc(media), filename: shareName }], shareName); if (r === "shared" || r === "cancelled") return; }
                shareWhatsApp({ url: mediaSrc(media), text: shareName });
              }}>
                <MessageCircle className="h-4 w-4 mr-2" /> WhatsApp
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="video-copy-link" onClick={async () => { if (await copyLink(mediaSrc(media))) toast.success(t("link_copied")); }}>
                <Link2 className="h-4 w-4 mr-2" /> {t("copy_link")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <button
          data-testid="video-markup-button"
          onClick={captureFrame}
          className="flex items-center gap-1.5 rounded-full bg-white/15 hover:bg-white/25 text-white text-xs font-semibold px-3.5 py-2 transition-colors"
        >
          <PenLine className="h-4 w-4" /> {t("markup")}
        </button>
        {onSend && galleryAvailable() && (
          <label className="flex items-center gap-1.5 text-white text-xs font-semibold select-none cursor-pointer" data-testid="video-save-gallery-row">
            <Checkbox
              data-testid="video-save-gallery-toggle"
              checked={saveGallery}
              onCheckedChange={(v) => { setSaveGallery(!!v); setSaveToGalleryPref(!!v); }}
              className="border-white/70 data-[state=checked]:bg-primary"
            />
            {t("save_to_gallery")}
          </label>
        )}
        {onSend && (
          <button
            data-testid="video-send-button"
            onClick={onSend}
            className="flex items-center gap-1.5 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground text-xs font-bold px-4 py-2 transition-colors"
          >
            <Send className="h-4 w-4" /> {t("send")}
          </button>
        )}
        <button
          data-testid="video-viewer-close"
          onClick={onClose}
          className="h-9 w-9 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Caption sent with the video (above the player, like photos) */}
      {(message?.content || "").trim() && !onSend && (
        <div data-testid="video-viewer-caption" className="mx-3 mb-1 px-3 py-2 rounded-xl bg-white/10 text-white/95 text-sm leading-snug whitespace-pre-wrap break-words max-h-28 overflow-y-auto shrink-0">
          {message.content.trim()}
        </div>
      )}

      {/* Player */}
      <div className="flex-1 min-h-0 flex flex-col items-center justify-center px-2 pb-4 gap-3">
        <div className="relative max-w-full min-h-0 flex items-center justify-center">
          {focusReady && <video
            ref={videoRef}
            data-testid="video-viewer-player"
            src={mediaSrc(media)}
            controls
            autoPlay
            playsInline
            onLoadedMetadata={(e) => setDuration(e.target.duration || 0)}
            onTimeUpdate={(e) => setProgress(e.target.currentTime || 0)}
            className="max-h-[calc(100vh-180px)] max-w-full rounded-lg min-h-0"
          />}
          {/* Auto-translated captions (synced, shown in the viewer's language) */}
          {captionText && (
            <div
              className="absolute bottom-14 left-1/2 -translate-x-1/2 max-w-[92%] px-3.5 py-1.5 rounded-lg bg-black/75 text-white text-sm sm:text-base font-medium text-center leading-snug pointer-events-none"
              data-testid="video-caption-text"
            >
              {captionText}
            </div>
          )}
        </div>
        {/* Markup timeline: amber dots mark moments that already have annotations — tap to jump */}
        {markups.length > 0 && duration > 0 && (
          <div className="w-full max-w-2xl px-2 shrink-0" data-testid="video-markup-timeline">
            <div
              ref={barRef}
              onClick={onBarClick}
              className="relative h-2 rounded-full bg-white/20 cursor-pointer"
            >
              <div
                className="absolute left-0 top-0 h-full rounded-full bg-white/40"
                style={{ width: `${Math.min(100, (progress / duration) * 100)}%` }}
              />
              {markups.map((mk) => (
                <button
                  key={mk.id}
                  type="button"
                  data-testid="video-markup-dot"
                  title={fmtSecs(mk.time)}
                  onClick={(e) => { e.stopPropagation(); seekTo(mk.time); }}
                  className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-4 w-4 rounded-full bg-amber-400 border-2 border-black/60 hover:scale-125 transition-transform"
                  style={{ left: `${Math.min(100, (mk.time / duration) * 100)}%` }}
                />
              ))}
            </div>
            <div className="flex items-center justify-between mt-1.5">
              <span className="text-[11px] text-white/70">{fmtSecs(progress)} / {fmtSecs(duration)}</span>
              <span className="text-[11px] text-amber-300 font-medium" data-testid="video-markup-count">
                {markups.length} {t("markup").toLowerCase()}{markups.length > 1 ? "s" : ""}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Frame markup editor (pauses the video; resumes on close/send) */}
      <MarkupEditor
        open={!!frame}
        imageSrc={frame?.dataUrl}
        onSave={sendMarkup}
        onClose={resume}
      />
      <ForwardDialog open={forwardOpen} media={media ? [media] : []} onClose={() => setForwardOpen(false)} />
    </div>,
    document.body
  );
}
